import React, { useState, useEffect, useMemo } from 'react';
import { leaveTypesApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiPlus, HiPencil, HiX } from 'react-icons/hi';

export const FULL_DAY_LEAVE_NAME = 'Full Day Leave';
export const HALF_DAY_LEAVE_NAME = 'Half Day Leave';

const DEFAULT_FORM = {
  description: '',
  is_active: true,
  leaves_per_month: 2,
  probation_months: 2,
};

const isMonthlyLedger = (lt) =>
  lt.uses_monthly_ledger === 1 || lt.uses_monthly_ledger === true;

const isHalfDayCompanion = (lt) =>
  (lt.allows_half_day === 1 || lt.allows_half_day === true) &&
  !(lt.uses_monthly_ledger === 1 || lt.uses_monthly_ledger === true);

const isActive = (lt) =>
  lt.is_active === 1 || lt.is_active === true;

const buildUpdatePayload = (form, editingLeaveType) => ({
  name: editingLeaveType.name,
  description: form.description,
  is_active: form.is_active,
  leaves_per_month: form.leaves_per_month,
  probation_months: form.probation_months,
  uses_monthly_ledger: true,
  leaves_per_year: null,
  deduct_salary: false,
  allows_half_day: false,
  display_order: editingLeaveType.display_order ?? 1,
});

const LeaveTypesConfig = () => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const monthlyLeaveType = useMemo(
    () => leaveTypes.find(isMonthlyLedger) || null,
    [leaveTypes]
  );

  const halfDayLeaveType = useMemo(
    () => leaveTypes.find(isHalfDayCompanion) || null,
    [leaveTypes]
  );

  const summaryLeavesPerMonth = monthlyLeaveType?.leaves_per_month ?? 2;
  const summaryProbationMonths = monthlyLeaveType?.probation_months ?? 2;

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const fetchLeaveTypes = async () => {
    setLoading(true);
    try {
      const response = await leaveTypesApi.getAll();
      setLeaveTypes(response.data.data);
    } catch {
      toast.error('Failed to fetch leave policy');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingLeaveType(null);
    setFormData(DEFAULT_FORM);
    setShowForm(true);
  };

  const handleEdit = (leaveType) => {
    setEditingLeaveType(leaveType);
    setFormData({
      description: leaveType.description || '',
      is_active: isActive(leaveType),
      leaves_per_month: leaveType.leaves_per_month ?? 2,
      probation_months: leaveType.probation_months ?? 2,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingLeaveType) {
        await leaveTypesApi.update(
          editingLeaveType.id,
          buildUpdatePayload(formData, editingLeaveType)
        );
        toast.success('Monthly leave policy updated');
      } else {
        await leaveTypesApi.setupMonthlyPair({
          description: formData.description?.trim() || 'Full day leave (counts as 1 day)',
          is_active: formData.is_active,
          leaves_per_month: formData.leaves_per_month,
          probation_months: formData.probation_months,
        });
        toast.success('Full Day Leave and Half Day Leave created');
      }
      setShowForm(false);
      fetchLeaveTypes();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save leave policy');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (leaveType) => {
    try {
      await leaveTypesApi.update(leaveType.id, {
        is_active: !isActive(leaveType),
      });
      toast.success('Leave type updated');
      fetchLeaveTypes();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update leave type');
    }
  };

  const handleAddHalfDayCompanion = async () => {
    setSaving(true);
    try {
      await leaveTypesApi.createHalfDayCompanion();
      toast.success('Half Day Leave added');
      fetchLeaveTypes();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add Half Day Leave');
    } finally {
      setSaving(false);
    }
  };

  const policyRows = useMemo(() => {
    const rows = [];
    if (monthlyLeaveType) {
      rows.push({ ...monthlyLeaveType, countsAs: '1 day', isPolicy: true });
    }
    if (halfDayLeaveType) {
      rows.push({ ...halfDayLeaveType, countsAs: '0.5 day', isPolicy: false });
    }
    return rows;
  }, [monthlyLeaveType, halfDayLeaveType]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Monthly Leave Policy</h1>
          <p className="text-gray-500 mt-1">
            Full Day and Half Day leave types share one monthly balance
          </p>
        </div>
        {!monthlyLeaveType && (
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <HiPlus className="w-5 h-5" />
            Set up monthly leave
          </button>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <p className="font-semibold mb-2">How it works</p>
        <ul className="space-y-1 text-blue-800 list-disc list-inside">
          <li>
            <strong>{summaryLeavesPerMonth}</strong> leave{summaryLeavesPerMonth === 1 ? '' : 's'} credited each month
          </li>
          <li>
            First <strong>{summaryProbationMonths}</strong> month{summaryProbationMonths === 1 ? '' : 's'} after join:
            cannot use leave; accrues for exit salary (
            {summaryLeavesPerMonth * summaryProbationMonths} day
            {summaryLeavesPerMonth * summaryProbationMonths === 1 ? '' : 's'} at defaults)
          </li>
          <li>
            <strong>{FULL_DAY_LEAVE_NAME}</strong> counts as 1 day; <strong>{HALF_DAY_LEAVE_NAME}</strong> counts as 0.5 day
          </li>
          <li>Unused balance carries forward month to month</li>
        </ul>
      </div>

      {monthlyLeaveType && !halfDayLeaveType && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-amber-900">
            Half Day Leave is missing. Staff need both types to request full-day or half-day leave.
          </p>
          <button
            type="button"
            disabled={saving}
            onClick={handleAddHalfDayCompanion}
            className="shrink-0 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm font-medium"
          >
            Add Half Day Leave
          </button>
        </div>
      )}

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left p-4 font-semibold text-gray-700">Name</th>
                <th className="text-left p-4 font-semibold text-gray-700">Description</th>
                <th className="text-center p-4 font-semibold text-gray-700">Counts as</th>
                <th className="text-center p-4 font-semibold text-gray-700">Leaves/Month</th>
                <th className="text-center p-4 font-semibold text-gray-700">Probation (months)</th>
                <th className="text-center p-4 font-semibold text-gray-700">Status</th>
                <th className="text-right p-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {policyRows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center p-8 text-gray-500">
                    <p className="mb-3">No monthly leave policy configured yet.</p>
                    <button
                      type="button"
                      onClick={openCreate}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                    >
                      <HiPlus className="w-4 h-4" />
                      Set up monthly leave
                    </button>
                  </td>
                </tr>
              ) : (
                policyRows.map((row) => (
                  <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-4 font-medium text-gray-900">{row.name}</td>
                    <td className="p-4 text-gray-600">{row.description || '—'}</td>
                    <td className="p-4 text-center text-gray-700 font-medium">{row.countsAs}</td>
                    <td className="p-4 text-center text-gray-700 font-medium">
                      {row.isPolicy ? (row.leaves_per_month ?? 2) : '—'}
                    </td>
                    <td className="p-4 text-center text-gray-700 font-medium">
                      {row.isPolicy ? (row.probation_months ?? 2) : '—'}
                    </td>
                    <td className="p-4 text-center">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(row)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isActive(row)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {isActive(row) ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        {row.isPolicy && (
                          <button
                            type="button"
                            onClick={() => handleEdit(row)}
                            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                            title="Edit policy"
                          >
                            <HiPencil className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div
          className="modal-shell flex items-center justify-center"
          onClick={() => setShowForm(false)}
        >
          <div className="modal-panel max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex-shrink-0 p-4 sm:p-5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingLeaveType ? 'Edit monthly leave policy' : 'Set up monthly leave'}
              </h3>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>

            <div className="modal-body">
              <form id="leave-type-form" onSubmit={handleSubmit} className="space-y-3">
                {!editingLeaveType && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    <p className="font-medium text-gray-900">Creates two leave types:</p>
                    <p className="mt-1">{FULL_DAY_LEAVE_NAME} (1 day) and {HALF_DAY_LEAVE_NAME} (0.5 day)</p>
                  </div>
                )}

                {editingLeaveType && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Leave type</label>
                    <input
                      type="text"
                      value={editingLeaveType.name}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={editingLeaveType ? 'Optional description' : 'Description for Full Day Leave'}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Leaves per month</label>
                    <input
                      type="number"
                      value={formData.leaves_per_month}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          leaves_per_month: parseInt(e.target.value, 10) || 2,
                        })
                      }
                      min="1"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Probation months</label>
                    <input
                      type="number"
                      value={formData.probation_months}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          probation_months: parseInt(e.target.value, 10) || 2,
                        })
                      }
                      min="1"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Cannot use leave during probation; accrues for exit payout
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between py-2.5 px-3 border border-gray-200 rounded-lg">
                  <div className="min-w-0 flex-1 pr-3">
                    <span className="text-sm font-medium text-gray-900">Active</span>
                    <p className="text-xs text-gray-500">
                      {editingLeaveType ? 'Staff can request this leave type when active' : 'Both leave types will be active'}
                    </p>
                  </div>
                  <label className="relative inline-flex shrink-0 items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                  </label>
                </div>
              </form>
            </div>

            <div className="modal-footer flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="leave-type-form"
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : editingLeaveType ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveTypesConfig;
